import { Component, type ReactNode } from 'react';
import { Button } from './ui/Button';

/**
 * Catches render-time errors in the routed content so a thrown page renders a
 * styled fallback instead of white-screening the whole app. Wrap it around an
 * <Outlet> and pass `key={location.pathname}` so navigating to another route
 * remounts it and clears the error.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: unknown) {
    // Surfaced for logging/observability; the UI shows the friendly fallback below.
    console.error('UI ErrorBoundary caught:', error, info);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="font-display text-5xl font-bold text-fg-subtle">Something broke</p>
        <div>
          <p className="text-fg">This screen hit an unexpected error.</p>
          <p className="mt-1 text-sm text-fg-muted">It has been logged. Try reloading, or head back.</p>
        </div>
        {import.meta.env.DEV && (
          <pre className="max-w-xl overflow-auto rounded-lg border border-border bg-surface-2 p-3 text-left text-xs text-fg-muted">
            {error.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button onClick={() => { window.location.href = '/'; }}>Back to home</Button>
        </div>
      </div>
    );
  }
}
